import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MessageService } from 'primeng/api'
import { catchError, forkJoin, map, of, switchMap } from 'rxjs'

import { WorklogService, worklogErrorMessage } from '../../data/services/worklog.service'
import { WorkItem } from '../../data/interfaces/worklog.interface'
import { PRIORITY_META, PRIORITY_ORDER, SEVERITY_META, TYPE_META } from '../../data/constants/worklog.constants'
import { fileIconOf, fileSizeLabel } from '../../data/utils/attachment.util'
import { pasteKeyLabel } from '@data/utils/shortcut-label.util'

//* Campos del intake donde se puede pegar una imagen. Se listan aquí porque `uploadPending` tiene
//* que recorrerlos todos para cambiar los tokens por las URLs: agregar un campo nuevo sin sumarlo a
//* esta lista dejaría sus imágenes apuntando a un token que nadie resuelve.
const TEXT_FIELDS = ['description', 'acceptanceCriteria', 'stepsToReproduce', 'expectedResult'] as const

/**
 * Diálogo de alta de un item en el Backlog. Abierto a todos los miembros internos
 * (RBAC `worklog.create`). El backend fija `createdBy` = usuario autenticado.
 *
 * Captura SOLO lo que sabe quien reporta: qué necesita y con qué urgencia. Las áreas técnicas,
 * el esfuerzo, el responsable, la fecha de entrega y el `origin` los decide el equipo (el backend
 * los ignora si llegan en el alta). Motivo: quien levanta la tarea no distingue frontend de
 * backend —conoce "la plataforma" y "el canal"— ni tiene con qué puntuar; pedírselo solo produce
 * datos inventados en las métricas.
 */
@Component({
  selector: 'app-worklog-create-dialog',
  templateUrl: './worklog-create-dialog.component.html',
  styleUrls: ['./worklog-create-dialog.component.scss'],
})
export class WorklogCreateDialogComponent implements OnInit {
  @Input() visible = false
  @Output() visibleChange = new EventEmitter<boolean>()
  @Output() created = new EventEmitter<WorkItem>()

  form!: FormGroup
  saving = false

  //* Imágenes pegadas en los textos ANTES de que la tarea exista. No hay a qué colgarlas todavía,
  //* así que se retienen aquí y en el texto queda un token `(adjunto:N)`; al crearse la tarea se
  //* suben y el token se cambia por la URL real. Subir primero y crear después no es opción: el
  //* endpoint de adjuntos necesita el id del item.
  pendingImages: { token: string; file: File }[] = []

  //* Archivos del campo «Archivos». Mismo problema que las imágenes —no hay id al que colgarlos—,
  //* pero se suben como adjuntos sueltos (`inline: false`): no los menciona ningún texto.
  pendingFiles: File[] = []

  /**
   * Folio de la tarea recién creada. Mientras tiene valor, el diálogo muestra la confirmación
   * en vez del formulario: es el momento en que quien reporta puede copiarlo, y si se cierra el
   * modal de golpe (como hacía antes) no vuelve a verlo sin ir a buscarlo.
   */
  createdCode: string | null = null
  copiado = false

  readonly typeOptions = [
    { label: TYPE_META.task.label, value: 'task', icon: TYPE_META.task.icon, color: TYPE_META.task.color },
    { label: TYPE_META.bug.label, value: 'bug', icon: TYPE_META.bug.icon, color: TYPE_META.bug.color },
  ]
  readonly priorityOptions = PRIORITY_ORDER.map((p) => ({ label: PRIORITY_META[p].label, value: p }))
  readonly severityOptions = (['critical', 'high', 'medium', 'low'] as const).map((s) => ({ label: SEVERITY_META[s].label, value: s }))

  constructor(private fb: FormBuilder, private worklog: WorklogService, private messageService: MessageService) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      type: ['task', Validators.required],
      title: ['', [Validators.required, Validators.maxLength(160)]],
      priority: [null],
      severity: [null],
      description: [''],
      acceptanceCriteria: [''],
      stepsToReproduce: [''],
      expectedResult: [''],
    })
  }

  get isBug(): boolean {
    return this.form?.get('type')?.value === 'bug'
  }

  /** Atajo de pegado según el sistema. La lógica vive en `shortcut-label.util` porque el reporte
   *  rápido de problemas muestra el mismo dato. */
  get pasteKey(): string {
    return pasteKeyLabel()
  }

  close(): void {
    this.visible = false
    this.visibleChange.emit(false)
    //* La confirmación se limpia al cerrar, no al abrir: si se limpiara al abrir, reabrir el
    //* diálogo mostraría por un instante el folio de la tarea anterior.
    this.createdCode = null
    this.copiado = false
  }

  /** Copia el folio al portapapeles — patrón del repo: `navigator.clipboard` + feedback local. */
  copyCode(): void {
    if (!this.createdCode) return
    navigator.clipboard.writeText(this.createdCode)
    this.copiado = true
    setTimeout(() => (this.copiado = false), 2000)
  }

  /** Registra otra sin salir: el formulario ya quedó limpio tras crear. */
  otra(): void {
    this.createdCode = null
    this.copiado = false
  }

  submit(): void {
    if (this.form.invalid || this.saving) {
      this.form.markAllAsTouched()
      return
    }
    this.saving = true
    const raw = this.form.value
    const dto: Partial<WorkItem> = {
      type: raw.type,
      title: raw.title.trim(),
      description: raw.description?.trim() || '',
      acceptanceCriteria: raw.acceptanceCriteria?.trim() || '',
      ...(raw.priority ? { priority: raw.priority } : {}),
      //* Los campos de bug viajan solo en bugs: el form los conserva ocultos al cambiar de
      //* tipo, y sin este guard un task heredaba el expectedResult capturado como bug.
      ...(this.isBug && raw.severity ? { severity: raw.severity } : {}),
      ...(this.isBug && raw.stepsToReproduce ? { stepsToReproduce: raw.stepsToReproduce.trim() } : {}),
      ...(this.isBug && raw.expectedResult ? { expectedResult: raw.expectedResult.trim() } : {}),
    }
    this.worklog
      .createItem(dto)
      .pipe(switchMap((item) => (item ? forkJoin([this.uploadPending(item, dto), this.uploadFiles(item)]).pipe(map(() => item)) : of(null))))
      .subscribe({
        next: (item) => {
          this.saving = false
          if (!item) return
          //* El folio se muestra al crear, no solo se guarda: es el identificador con el que se
          //* encarga y se consulta la tarea ("resuelve la F-012"), y hasta ahora el alta lo
          //* generaba sin devolvérselo a quien iba a necesitarlo.
          this.createdCode = item.code ?? null
          this.pendingImages = []
          this.pendingFiles = []
          this.created.emit(item)
          this.form.reset({ type: 'task' })
        },
        error: (err) => {
          this.saving = false
          this.messageService.add({ key: 'worklog', severity: 'error', summary: 'No se pudo crear', detail: worklogErrorMessage(err, 'No se pudo crear la tarea.'), life: 5000 })
        },
      })
  }

  /**
   * El editor ya insertó la imagen donde estaba el cursor y le puso su token; aquí solo se retiene
   * el archivo. No se puede subir todavía: el endpoint de adjuntos necesita el id del item y la
   * tarea aún no existe, así que la subida se hace al crearla (`uploadPending`).
   *
   * Una captura de la pantalla que falla vale más que tres párrafos describiéndola, y hasta que
   * esto existió el único camino era mandarla por otro canal y que no llegara nunca a la tarea.
   */
  onImagePasted(pasted: { token: string; file: File }): void {
    this.pendingImages.push(pasted)
  }

  /** El editor quitó la imagen del texto; aquí se saca de la cola para no subir un huérfano. */
  onImageRemoved(token: string): void {
    this.pendingImages = this.pendingImages.filter((p) => p.token !== token)
  }

  addFiles(files: FileList | null): void {
    if (files?.length) this.pendingFiles = [...this.pendingFiles, ...Array.from(files)]
  }

  //* Sin el `preventDefault` el navegador ABRE el archivo soltado y se pierde lo capturado.
  onFilesDropped(event: DragEvent): void {
    event.preventDefault()
    this.addFiles(event.dataTransfer?.files ?? null)
  }

  removeFile(index: number): void {
    this.pendingFiles = this.pendingFiles.filter((_, i) => i !== index)
  }

  fileIcon(f: File): string {
    return fileIconOf(f.type, f.name)
  }

  fileSize(bytes: number): string {
    return fileSizeLabel(bytes)
  }

  /**
   * Sube los archivos retenidos ahora que la tarea existe. Un archivo que falla NO tumba el alta:
   * la tarea ya se creó y su folio es lo importante; se avisa cuál faltó para subirlo desde el
   * detalle, en vez de fingir que todo salió o revertir una tarea válida.
   */
  private uploadFiles(item: WorkItem) {
    if (!this.pendingFiles.length) return of(null)
    const uploads = this.pendingFiles.map((file) =>
      this.worklog.uploadAttachment(item._id, file, { inline: false }).pipe(
        map((att) => (att ? null : file.name)),
        catchError(() => of(file.name))
      )
    )
    return forkJoin(uploads).pipe(
      map((fallidos) => {
        const nombres = fallidos.filter((n): n is string => !!n)
        if (nombres.length) {
          this.messageService.add({
            key: 'worklog',
            severity: 'warn',
            summary: 'Algunos archivos no se subieron',
            detail: `La tarea se creó, pero faltaron: ${nombres.join(', ')}. Súbelos desde su detalle.`,
            life: 7000,
          })
        }
        return null
      })
    )
  }

  /**
   * Sube las imágenes retenidas ahora que la tarea existe y cambia cada token por su URL.
   *
   * Va en dos pasos (subir todas, luego un solo PATCH) porque parchear por imagen dejaría la
   * descripción a medio resolver si la segunda subida falla. Si una subida falla, su token se
   * queda en el texto: es visible y se puede corregir a mano, que es mejor que borrarlo en
   * silencio y perder el rastro de que ahí iba una imagen.
   */
  private uploadPending(item: WorkItem, dto: Partial<WorkItem>) {
    //* Solo se suben las imágenes cuya marca SIGUE en algún texto. La ✕ del editor avisa por
    //* `imageRemoved`, pero un Backspace sobre la imagen seleccionada la borra sin avisar a nadie:
    //* sin este filtro esa imagen se subía igual y aparecía en Adjuntos sin que ningún texto la
    //* mencionara. Se comprueba contra el dto —lo que de verdad se guardó—, no contra el form.
    const enUso = this.pendingImages.filter((p) => TEXT_FIELDS.some((f) => ((dto as any)[f] ?? '').includes(`(${p.token})`)))
    if (!enUso.length) return of(null)
    const uploads = enUso.map((p) =>
      this.worklog.uploadAttachment(item._id, p.file, { inline: true }).pipe(map((att) => ({ token: p.token, url: att?.url ?? null })))
    )
    return forkJoin(uploads).pipe(
      switchMap((results) => {
        const resolved = results.filter((r) => !!r.url)
        if (!resolved.length) return of(null)
        const patch: Record<string, string> = {}
        for (const field of TEXT_FIELDS) {
          const original: string = (dto as any)[field] ?? ''
          if (!original) continue
          let next = original
          //* `split`/`join` en vez de replace con regex: la URL del bucket trae `$` y `&` que un
          //* patrón de reemplazo interpretaría como referencias de captura.
          for (const r of resolved) next = next.split(`(${r.token})`).join(`(${r.url})`)
          if (next !== original) patch[field] = next
        }
        return Object.keys(patch).length ? this.worklog.updateItem(item._id, patch as Partial<WorkItem>) : of(null)
      })
    )
  }
}
